import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { share } from "@src/appV2/NativeAdapters/share.adapter";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Illustration, Text } from "@src/appV2/redesign/system/ui-components";
import { useCreateShareLink } from "@src/appV2/Referral/api/useCreateShareLink";
import { type ReactNode } from "react";

interface ReferralShareContentProps {
  title: string;
  description: ReactNode;
  shareText: (referralLink: string, amount: number) => string;
  viewedEvent: string;
  shareClickedEvent: string;
  analyticsContext?: Record<string, unknown>;
}

export function ReferralShareContent(props: ReferralShareContentProps) {
  const { title, description, shareText, viewedEvent, shareClickedEvent, analyticsContext } = props;

  const { mutateAsync: createShareLink, isLoading } = useCreateShareLink();

  const { amount: referralAmount } = useCbhFlag(CbhFeatureFlag.OFFER_OUTCOME_REFERRAL_AMOUNT, {
    defaultValue: { enabled: false, amount: 20 },
  });

  useLogEffect(viewedEvent, analyticsContext);

  async function handleShareClick() {
    try {
      logEvent(shareClickedEvent, analyticsContext);

      const { data: referralLinkDataArray } = await createShareLink({ isReferredToHiring: true });

      // eslint-disable-next-line prefer-destructuring
      const referralLinkData = referralLinkDataArray[0];

      if (isDefined(referralLinkData)) {
        await share({
          title: "Join Clipboard",
          text: shareText(referralLinkData.attributes.referralLink, referralAmount),
        });
      }
    } catch {
      // Ignore errors from share dialog dismissal
    }
  }

  return (
    <>
      <Stack
        spacing={3}
        alignItems="center"
        justifyContent="center"
        sx={{ paddingX: 8, paddingTop: 8, paddingBottom: 4 }}
      >
        <Box sx={{ transform: "scale(1.2)", transformOrigin: "center" }}>
          <Illustration type="referral-bonus" />
        </Box>
        <Stack spacing={2} textAlign="center">
          <Text variant="h3">{title}</Text>
          <Text>{description}</Text>
          <Text>
            You both earn{" "}
            <Box
              component="span"
              sx={(theme) => ({
                backgroundColor: theme.palette.intent?.success.background,
                color: theme.palette.intent?.success.text,
                fontWeight: 500,
                paddingX: 1,
                paddingY: 0.25,
                borderRadius: "100px",
              })}
            >
              ${referralAmount}
            </Box>{" "}
            after their first interview.
          </Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="horizontal" sx={{ paddingX: 8, paddingBottom: 8, paddingTop: 4 }}>
        <Stack spacing={4} width="100%">
          <LoadingButton
            variant="contained"
            size="large"
            isLoading={isLoading}
            onClick={() => {
              handleShareClick();
            }}
          >
            Share referral link
          </LoadingButton>
          <Text
            variant="caption"
            color={(theme) => theme.palette.text.tertiary}
            sx={{ textAlign: "center" }}
          >
            Bonus paid per referral after their first confirmed interview attendance through
            Clipboard Health. T&Cs apply.
          </Text>
        </Stack>
      </DialogFooter>
    </>
  );
}
